import { Stack } from '@chakra-ui/react';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import { Radio, RadioGroup, Text } from '@revelio/ui';

import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { UserPreferenceKey } from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.ContingentWorkers,
  label: 'Contingent Workers',
};

export default function ContingentWorkersEditor({
  affectedScope,
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const [includeContingent, setIncludeContingent] = useState(() =>
    preferences[UserPreferenceKey.ContingentWorkers] ? 'include' : 'exclude'
  );

  useEffect(() => {
    setIncludeContingent(
      preferences[UserPreferenceKey.ContingentWorkers] ? 'include' : 'exclude'
    );
  }, [preferences]);

  const onUpdate = useCallback(
    (value: string) => {
      onPreferenceUpdate?.(
        UserPreferenceKey.ContingentWorkers,
        value === 'include'
      );
      setIncludeContingent(value);
      PreferenceBrowserStorage.update(
        affectedScope,
        UserPreferenceKey.ContingentWorkers,
        value === 'include'
      );
    },
    [affectedScope, onPreferenceUpdate]
  );

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Workforce Segment"
        indicateDiff={diff?.includes(meta.id) ?? false}
      />
      <RadioGroup
        value={includeContingent}
        onChange={onUpdate}
        className={styles.radioGroup}
      >
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Radio value="include" className={styles.appearanceRadio}>
              Full Workforce
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Full Workforce includes all workers: employees, contractors,
              consultants, and other contingent workers who may not appear in
              official company reports.
            </Text>
          </Stack>
          <Stack spacing={1}>
            <Radio value="exclude" className={styles.appearanceRadio}>
              Exclude Contingent
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Exclude Contingent shows only traditional employees, excluding
              contractors, freelancers, and temporary staff.
            </Text>
          </Stack>
        </Stack>
      </RadioGroup>
    </>
  );
}
